import React, { useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router-dom';
import { Box, VStack, Spinner, Text, Alert, AlertIcon, SimpleGrid, Heading, HStack, Badge, Tooltip, Button, Divider } from '@chakra-ui/react';
import { AppContext } from '../AppContext';
import { callApi } from '../../callApi';
import HeaderMenus from '../HeaderMenus';
import CaseHeaderPanel from './CaseHeaderPanel';
import MedicalNecessityPanel from './MedicalNecessityPanel';

const USNV2Compare = () => {
  const { caseId } = useParams();
  const { accessToken } = useContext(AppContext);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [caseData, setCaseData] = useState(null);
  const [v1Results, setV1Results] = useState(null);
  const [v2Results, setV2Results] = useState(null);
  
  const [comparisonData, setComparisonData] = useState(null);
  const [generatingComparison, setGeneratingComparison] = useState(false);
  const [comparisonError, setComparisonError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!caseId || !accessToken) return;
      
      setLoading(true);
      setError(null);
      
      try {
        // 1. Fetch Case Details for the Header
        const detailsResponse = await callApi(`/get_case_details/${caseId}`, accessToken);
        if (detailsResponse.ok) {
          const detailsData = await detailsResponse.json();
          setCaseData(detailsData);
        } else {
          throw new Error('Failed to fetch case details');
        }

        // 2. Fetch V1 (Legacy) Results
        const v1Response = await callApi(`/usn/analyze_case/${caseId}?claim_type=pro`, accessToken, { method: 'GET' });
        if (v1Response.ok) {
          const v1Data = await v1Response.json();
          if (v1Data.status === 'success') {
            setV1Results(v1Data.results);
          }
        }

        // 3. Fetch V3 Beta Results (See features/usn-v3-pipeline-comparison.md)
        const v2Response = await callApi(`/usn/v3/analyze_case/${caseId}?claim_type=pro`, accessToken, { method: 'GET' });
        if (v2Response.ok) {
          const v2Data = await v2Response.json();
          if (v2Data.status === 'success') {
            setV2Results(v2Data.results);
          }
        }

        // 4. Fetch Existing Comparison (if any)
        const compResponse = await callApi(`/usn/v2/compare_runs/${caseId}?claim_type=pro`, accessToken, { method: 'GET' });
        if (compResponse.ok) {
          const compData = await compResponse.json();
          if (compData.status === 'success') {
            setComparisonData(compData);
          }
        }

      } catch (err) {
        console.error("Error fetching comparison data:", err);
        setError(err.message || 'An error occurred while fetching data.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [caseId, accessToken]);

  if (loading) {
    return (
      <Box p={8} textAlign="center">
        <Spinner size="xl" />
        <Text mt={4}>Loading comparison data...</Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={8}>
        <Alert status="error">
          <AlertIcon />
          {error}
        </Alert>
      </Box>
    );
  }

  const handleGenerateComparison = async () => {
    setGeneratingComparison(true);
    setComparisonError(null);
    try {
      const response = await callApi('/usn/v2/compare_runs', accessToken, {
        method: 'POST',
        body: JSON.stringify({ external_case_id: caseId, claim_type: 'pro' })
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.status === 'success') {
          setComparisonData(data);
        } else {
          setComparisonError(data.error_message || 'Failed to generate comparison.');
        }
      } else {
        setComparisonError('Server error while generating comparison.');
      }
    } catch (err) {
      setComparisonError(err.message || 'An error occurred.');
    } finally {
      setGeneratingComparison(false);
    }
  };

  const v1Codes = v1Results?.ordered_diagnoses?.map(d => d.code) || [];
  const v2Codes = v2Results?.ordered_diagnoses?.map(d => d.code) || [];
  const sharedCodes = v1Codes.filter(code => v2Codes.includes(code));

  return (

    <Box minH="100vh" bg="gray.50">
      <HeaderMenus />
      
      <Box maxW="1600px" mx="auto" p={4}>
        <VStack spacing={6} align="stretch">
          
          <HStack justify="space-between" align="center">
            <Heading size="lg" color="gray.700">USN Pipeline Comparison</Heading>
            <Badge colorScheme="purple" p={2} borderRadius="md">A/B Testing Mode</Badge>
          </HStack>

          {caseData && (
            <CaseHeaderPanel caseData={caseData} />
          )}

          <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={8}>
            
            {/* Left Column: V1 Legacy */}
            <Box bg="white" p={4} borderRadius="lg" shadow="sm" border="1px solid" borderColor="gray.200">
              <VStack align="stretch" spacing={4}>
                <Box borderBottom="2px solid" borderColor="gray.100" pb={2}>
                  <Heading size="md" color="gray.600">Current</Heading>
                  <Text fontSize="sm" color="gray.500">Legacy Pipeline</Text>
                </Box>
                
                {v1Results ? (
                  <MedicalNecessityPanel results={v1Results} highlightCodes={sharedCodes} />
                ) : (
                  <Alert status="warning">
                    <AlertIcon />
                    No V1 results found for this case.
                  </Alert>
                )}
              </VStack>
            </Box>

            {/* Right Column: V3 Pipeline */}
            <Box bg="white" p={4} borderRadius="lg" shadow="sm" border="2px solid" borderColor="purple.200">
              <VStack align="stretch" spacing={4}>
                <Box borderBottom="2px solid" borderColor="purple.100" pb={2}>
                  <HStack justify="space-between">
                    <Box>
                      <Heading size="md" color="purple.700">V3 (Beta)</Heading>
                      <Text fontSize="sm" color="gray.500">V3 Pipeline</Text>
                    </Box>
                    <HStack spacing={2}>
                      {v2Results?.approval_assessment_score !== undefined && (
                        <Badge colorScheme={v2Results.approval_assessment_score >= 70 ? 'green' : 'red'} fontSize="md" p={1}>
                          Approval: {v2Results.approval_assessment_score}
                        </Badge>
                      )}
                      {v2Results?.needs_review_score !== undefined && (
                        <Tooltip label={v2Results.needs_review_rationale || "No rationale provided"} placement="bottom" hasArrow>
                          <Badge 
                            colorScheme={v2Results.needs_review_score < 30 ? 'green' : 'red'} 
                            fontSize="md" 
                            p={1}
                            cursor="help"
                          >
                            QA Score: {v2Results.needs_review_score}
                          </Badge>
                        </Tooltip>
                      )}
                    </HStack>
                  </HStack>
                </Box>
                
                {v2Results ? (
                  <MedicalNecessityPanel results={v2Results} hideScores={true} highlightCodes={sharedCodes} />
                ) : (
                  <Alert status="warning">
                    <AlertIcon />
                    No V3 results found for this case.
                  </Alert>
                )}
              </VStack>
            </Box>

          </SimpleGrid>

          {/* LLM Comparison Section */}
          <Box bg="white" p={6} borderRadius="lg" shadow="sm" border="1px solid" borderColor="gray.200" mt={8}>
            <VStack align="stretch" spacing={4}>
              <HStack justify="space-between">
                <Heading size="md" color="gray.700">LLM Auditor Comparison</Heading>
                {!comparisonData && (
                  <Button 
                    colorScheme="purple" 
                    onClick={handleGenerateComparison} 
                    isLoading={generatingComparison}
                    loadingText="Analyzing..."
                  >
                    Generate LLM Comparison
                  </Button>
                )}
              </HStack>
              
              {comparisonError && (
                <Alert status="error" borderRadius="md">
                  <AlertIcon />
                  {comparisonError}
                </Alert>
              )}

              {comparisonData && comparisonData.llm_analysis && (
                <VStack align="stretch" spacing={6} mt={4}>
                  <HStack justify="space-between" p={4} bg="gray.50" borderRadius="md" border="1px solid" borderColor="gray.200">
                    <Box>
                      <Text fontWeight="bold" color="gray.600">Winner</Text>
                      <Badge colorScheme={(comparisonData.llm_analysis.winner === 'V2' || comparisonData.llm_analysis.winner === 'V3') ? 'purple' : comparisonData.llm_analysis.winner === 'V1' ? 'blue' : 'gray'} fontSize="lg" p={2} mt={1}>
                        {comparisonData.llm_analysis.winner === 'V2' ? 'V3' : comparisonData.llm_analysis.winner}
                      </Badge>
                    </Box>
                    <Box textAlign="right">
                      <Text fontWeight="bold" color="gray.600">V3 Preference Score</Text>
                      <Text fontSize="2xl" fontWeight="bold" color={comparisonData.llm_analysis.overall_preference_score >= 60 ? 'green.500' : 'red.500'}>
                        {comparisonData.llm_analysis.overall_preference_score} / 100
                      </Text>
                    </Box>
                  </HStack>

                  <Box>
                    <Text fontWeight="bold" mb={2}>Clinical Baseline</Text>
                    <Text color="gray.700">{comparisonData.llm_analysis.clinical_baseline}</Text>
                  </Box>

                  <Divider />

                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                    <Box>
                      <Text fontWeight="bold" color="blue.600" mb={2}>V1 Evaluation</Text>
                      <Text fontSize="sm" fontWeight="bold" mt={2}>Necessity Strength:</Text>
                      <Text fontSize="sm" color="gray.700">{comparisonData.llm_analysis.v1_evaluation?.necessity_rationale}</Text>
                      <Text fontSize="sm" fontWeight="bold" mt={2}>Defensibility:</Text>
                      <Text fontSize="sm" color="gray.700">{comparisonData.llm_analysis.v1_evaluation?.defensibility_rationale}</Text>
                    </Box>
                    <Box>
                      <Text fontWeight="bold" color="purple.600" mb={2}>V3 Evaluation</Text>
                      <Text fontSize="sm" fontWeight="bold" mt={2}>Necessity Strength:</Text>
                      <Text fontSize="sm" color="gray.700">{comparisonData.llm_analysis.v2_evaluation?.necessity_rationale}</Text>
                      <Text fontSize="sm" fontWeight="bold" mt={2}>Defensibility:</Text>
                      <Text fontSize="sm" color="gray.700">{comparisonData.llm_analysis.v2_evaluation?.defensibility_rationale}</Text>
                    </Box>
                  </SimpleGrid>

                  <Divider />

                  <Box>
                    <Text fontWeight="bold" mb={2}>Comparison Synthesis</Text>
                    <Text color="gray.700">{comparisonData.llm_analysis.comparison_synthesis}</Text>
                  </Box>
                  
                  <HStack justify="flex-end">
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={handleGenerateComparison} 
                      isLoading={generatingComparison}
                    >
                      Regenerate
                    </Button>
                  </HStack>
                </VStack>
              )}
            </VStack>
          </Box>

        </VStack>
      </Box>
    </Box>
  );
};

export default USNV2Compare;
